<template>
  <div
    class="gridElement event-action-row-layout"
    :class="{
      'event-action-row-layout--with-context': $slots.context != null,
      'event-action-row-layout--with-header-controls': $slots['header-controls'] != null,
      'event-action-row-layout--content-padded': paddedContent,
    }"
  >
    <div class="event-action-row-layout__side" :class="sideClass">
      <div class="event-action-row-layout__id">
        <slot name="id" />
      </div>
      <div class="event-action-row-layout__left">
        <slot name="left" />
      </div>
    </div>
    <div class="event-action-row-layout__header">
      <slot name="header" />
    </div>
    <div v-if="$slots['header-controls'] != null" class="event-action-row-layout__header-controls">
      <slot name="header-controls" />
    </div>
    <div v-if="$slots.context != null" class="event-action-row-layout__context">
      <slot name="context" />
    </div>
    <div class="event-action-row-layout__content">
      <slot name="content" />
    </div>
    <div class="event-action-row-layout__controls">
      <slot name="controls" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from "vue";

withDefaults(
  defineProps<{
    paddedContent?: boolean;
    sideClass?: HTMLAttributes["class"];
  }>(),
  {
    paddedContent: true,
    sideClass: undefined,
  },
);
</script>

<style lang="scss" scoped>
.gridElement.event-action-row-layout {
  display: grid;
  grid-template-areas:
    "side header controls"
    "side content controls";
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-rows: auto minmax(0, 1fr);

  &--with-context {
    grid-template-areas:
      "side header controls"
      "side context controls"
      "side content controls";
    grid-template-rows: auto auto minmax(0, 1fr);
  }

  &--with-header-controls {
    grid-template-areas:
      "side header header-controls"
      "side content controls";

    &.event-action-row-layout--with-context {
      grid-template-areas:
        "side header header-controls"
        "side context controls"
        "side content controls";
    }
  }
}

.event-action-row-layout__side {
  grid-area: side;
  display: flex;
  flex-direction: column;
}

.event-action-row-layout__id {
  flex: 0 0 auto;
}

.event-action-row-layout__header,
.event-action-row-layout__context,
.event-action-row-layout__content {
  min-width: 0;
  padding-inline: 0.5em;
}

.event-action-row-layout__header {
  grid-area: header;

  :deep(> .header) {
    padding: 0;
  }
}

.event-action-row-layout__left {
  flex: 1 1 auto;
  min-height: 0;
}

.event-action-row-layout__header-controls {
  grid-area: header-controls;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  padding-inline: 0.25rem;
}

.event-action-row-layout__context {
  grid-area: context;
  margin-block: 0.25rem;
}

.event-action-row-layout__content {
  grid-area: content;

  :deep(> .content) {
    padding: 0;
  }
}

.event-action-row-layout--content-padded .event-action-row-layout__content {
  padding-top: 0.5em;
  margin-bottom: 0.5em;
}

.event-action-row-layout__controls {
  grid-area: controls;
  padding: 0.25rem;
}

.event-action-row-layout--with-header-controls .event-action-row-layout__controls {
  padding-top: 0;
}

@media (max-width: 48rem) {
  .gridElement.event-action-row-layout {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .event-action-row-layout__header {
    min-height: 32px;
    margin-block: 0.25rem;

    :deep(> .header) {
      min-height: 0;
      padding-block: 0;
    }
  }

  .event-action-row-layout__header-controls {
    margin-block: 0.25rem;
  }
}
</style>
